import { DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { exportBikeStockCsv, fetchBikeStock, type BikeStockFilter, type BikeStockItem } from './api';

const { Text } = Typography;

export interface BikeStockDrawerProps {
  /** Which unsold bikes to list; null closes the drawer. */
  filter: BikeStockFilter | null;
  title: string;
  onClose: () => void;
}

/** The unsold bikes behind one age bucket, one row per chassis. */
export function BikeStockDrawer({ filter, title, onClose }: BikeStockDrawerProps): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { data, error, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['analytics', 'bikes', 'stock', filter],
    queryFn: ({ pageParam }) => fetchBikeStock(filter as BikeStockFilter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: filter !== null,
  });

  const rows = data?.pages.flatMap((page) => page.items) ?? [];

  const download = async (): Promise<void> => {
    if (filter === null) return;
    try {
      const csv = await exportBikeStockCsv(filter);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'unsold-bikes.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('insights.bikes.stock.downloadFailed'));
    }
  };

  const columns: TableProps<BikeStockItem>['columns'] = [
    {
      title: t('insights.bikes.stock.chassis'),
      key: 'chassis',
      render: (_: unknown, r) => <Text className="tabular">{r.chassisNo}</Text>,
    },
    {
      title: t('insights.bikes.stock.bike'),
      key: 'bike',
      render: (_: unknown, r) => (
        <Flex vertical>
          <Text className="tabular">{r.itemCode}</Text>
          {r.description !== null && <Text type="secondary">{r.description}</Text>}
        </Flex>
      ),
    },
    {
      title: t('insights.bikes.stock.brand'),
      key: 'brand',
      render: (_: unknown, r) => r.brand ?? t('insights.bikes.noBrand'),
    },
    {
      title: t('insights.bikes.stock.purchased'),
      key: 'purchased',
      render: (_: unknown, r) => (
        <Flex vertical>
          <span className="tabular">
            {r.purchasedOn === null ? '—' : dayjs(r.purchasedOn).format('D MMM YYYY')}
          </span>
          <Text type="secondary">{orDash(r.purchaseDocNo)}</Text>
        </Flex>
      ),
    },
    {
      title: t('insights.bikes.stock.days'),
      key: 'days',
      align: 'right',
      render: (_: unknown, r) => (r.daysInStock === null ? '—' : r.daysInStock),
    },
    {
      title: t('insights.bikes.stock.cost'),
      key: 'cost',
      align: 'right',
      render: (_: unknown, r) => (r.unitCost === null ? '—' : formatMoney(r.unitCost)),
    },
  ];

  return (
    <Drawer
      open={filter !== null}
      title={title}
      onClose={onClose}
      width={960}
      className="nks-action-drawer"
      extra={
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            void download();
          }}
        >
          {t('insights.lines.downloadCsv')}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {error !== null && (
          <Text type="danger">
            {error instanceof ApiError ? error.message : t('insights.bikes.stock.loadFailed')}
          </Text>
        )}
        <Table<BikeStockItem>
          rowKey="uid"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={filter !== null && isPending}
          locale={{ emptyText: t('insights.bikes.stock.empty') }}
          dataSource={rows}
          columns={columns}
        />
        {hasNextPage && (
          <Button
            block
            loading={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t('insights.bikes.stock.loadMore')}
          </Button>
        )}
      </Flex>
    </Drawer>
  );
}
